import React, { useState } from 'react';
import Button from '@salesforce/design-system-react/components/button';
import DataTable from '@salesforce/design-system-react/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/components/data-table/cell';
import Badge from '@salesforce/design-system-react/components/badge';
import Icon from '@salesforce/design-system-react/components/icon';
import Combobox from '@salesforce/design-system-react/components/combobox';

const OpenTestsComponent = ({ testGroups = [], onPassTest, onFailTest }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [testTypeFilter, setTestTypeFilter] = useState('all');

  // SAP Sales Order ID - same for all lines from this order
  const salesOrderNumber = '4500012345';

  // Flatten test groups to individual tests
  const allTests = testGroups.flatMap(testGroup => {
    const totalKg = testGroup.quantity * (testGroup.packSize || 1);
    return testGroup.individualTests
      .filter(test => test.status !== 'passed' && test.status !== 'failed')
      .map(test => ({
        testId: test.id,
        testGroupId: testGroup.id,
        testName: test.testName,
        testType: test.testType,
        status: test.status,
        result: test.result,
        testedBy: test.testedBy,
        productionOrderNumber: testGroup.productionOrderNumber,
        productName: testGroup.productName,
        itemCode: testGroup.itemCode,
        lineNumber: testGroup.lineNumber,
        quantity: testGroup.quantity,
        packSize: testGroup.packSize || 1,
        totalKg: totalKg,
        uom: testGroup.uom,
        warehouse: testGroup.warehouse || '01 - Hazlet',
        orderNumber: salesOrderNumber
      }));
  }
  );

  // Helper functions
  const getStatusBadgeColor = (status) => {
    const colorMap = {
      'not-started': 'light',
      'in-progress': 'default',
      'passed': 'success',
      'failed': 'error'
    };
    return colorMap[status] || 'light';
  };

  const getStatusLabel = (status) => {
    const labelMap = {
      'not-started': 'Not Started',
      'in-progress': 'In Progress',
      'passed': 'Passed',
      'failed': 'Failed'
    };
    return labelMap[status] || status;
  };

  // Filter tests based on search and filters
  const filteredTests = allTests.filter(test => {
    const matchesSearch = searchTerm === '' ||
      test.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      test.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      test.itemCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      test.testName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      test.productionOrderNumber.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || test.status === statusFilter;
    const matchesTestType = testTypeFilter === 'all' || test.testType === testTypeFilter;

    return matchesSearch && matchesStatus && matchesTestType;
  }).sort((a, b) => {
    // Sort by status: not-started first, then in-progress
    const statusOrder = {
      'not-started': 0,
      'in-progress': 1,
      'passed': 2,
      'failed': 3
    };
    return (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99);
  });

  // Custom DataTable Cells
  const OrderNumberCell = ({ item }) => (
    <DataTableCell>
      <span style={{ fontWeight: '500' }}>{item.orderNumber}</span>
    </DataTableCell>
  );
  OrderNumberCell.displayName = DataTableCell.displayName;

  const ProductCell = ({ item }) => (
    <DataTableCell>
      <div>
        <div style={{ fontWeight: '500' }}>{item.productName}</div>
        <div style={{ fontSize: '0.875rem', color: '#706e6b' }}>{item.itemCode}</div>
      </div>
    </DataTableCell>
  );
  ProductCell.displayName = DataTableCell.displayName;

  const ProductionOrderCell = ({ item }) => (
    <DataTableCell>
      <span style={{ fontWeight: '500' }}>{item.productionOrderNumber}</span>
    </DataTableCell>
  );
  ProductionOrderCell.displayName = DataTableCell.displayName;

  const TestNameCell = ({ item }) => (
    <DataTableCell>
      <div>
        <div style={{ fontWeight: '500' }}>{item.testName}</div>
        <div style={{ fontSize: '0.875rem', color: '#706e6b' }}>{item.testType}</div>
      </div>
    </DataTableCell>
  );
  TestNameCell.displayName = DataTableCell.displayName;

  const StatusCell = ({ item }) => (
    <DataTableCell>
      <Badge
        color={getStatusBadgeColor(item.status)}
        content={getStatusLabel(item.status)}
      />
    </DataTableCell>
  );
  StatusCell.displayName = DataTableCell.displayName;

  const ResultCell = ({ item }) => (
    <DataTableCell>
      <div>
        <span style={{ fontWeight: '500', color: '#706e6b' }}>{item.result || 'Awaiting test'}</span>
      </div>
    </DataTableCell>
  );
  ResultCell.displayName = DataTableCell.displayName;

  const TestedByCell = ({ item }) => (
    <DataTableCell>
      <div>
        <span style={{ color: '#706e6b' }}>{item.testedBy || 'Unassigned'}</span>
      </div>
    </DataTableCell>
  );
  TestedByCell.displayName = DataTableCell.displayName;

  const ActionsCell = ({ item }) => {
    const handlePass = () => {
      if (onPassTest) {
        onPassTest(item.testGroupId, item.testId);
      }
    };

    const handleFail = () => {
      if (onFailTest) {
        onFailTest(item.testGroupId, item.testId);
      }
    };

    return (
      <DataTableCell>
        <div style={{ display: 'flex', gap: '4px' }}>
          {(item.status === 'not-started' || item.status === 'in-progress') && (
            <>
              <Button
                label="Pass"
                variant="success"
                iconCategory="utility"
                iconName="success"
                iconPosition="left"
                onClick={handlePass}
              />
              <Button
                label="Fail"
                variant="destructive"
                iconCategory="utility"
                iconName="close"
                iconPosition="left"
                onClick={handleFail}
              />
            </>
          )}
        </div>
      </DataTableCell>
    );
  };
  ActionsCell.displayName = DataTableCell.displayName;

  return (
    <div>
      {/* Search and Filter Controls */}
      <div className="slds-p-around_medium slds-border_bottom">
        <div className="slds-grid slds-wrap slds-gutters">
          <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-3">
            <div className="slds-form-element">
              <label className="slds-form-element__label" htmlFor="search-input-tests">
                Search
              </label>
              <div className="slds-form-element__control slds-input-has-icon slds-input-has-icon_left">
                <Icon
                  assistiveText={{ label: 'Search' }}
                  category="utility"
                  name="search"
                  size="x-small"
                  className="slds-icon slds-input__icon slds-input__icon_left slds-icon-text-default"
                />
                <input
                  id="search-input-tests"
                  type="text"
                  className="slds-input"
                  placeholder="Search tests, products, or orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-3">
            <Combobox
              labels={{ label: 'Status' }}
              options={[
                { id: 'all', label: 'All Statuses' },
                { id: 'not-started', label: 'Not Started' },
                { id: 'in-progress', label: 'In Progress' }
              ]}
              selection={[{ id: statusFilter, label: statusFilter === 'all' ? 'All Statuses' : getStatusLabel(statusFilter) }]}
              onSelect={(_event, data) => {
                setStatusFilter(data.selection[0]?.id || 'all');
              }}
              variant="readonly"
            />
          </div>
          <div className="slds-col slds-size_1-of-1 slds-medium-size_1-of-3">
            <Combobox
              labels={{ label: 'Test Type' }}
              options={[
                { id: 'all', label: 'All Test Types' },
                { id: 'Quality Control', label: 'Quality Control' },
                { id: 'Microbiological', label: 'Microbiological' },
                { id: 'Physical Properties', label: 'Physical Properties' },
                { id: 'Chemical Analysis', label: 'Chemical Analysis' },
                { id: 'Stability Testing', label: 'Stability Testing' }
              ]}
              selection={[{ id: testTypeFilter, label: testTypeFilter === 'all' ? 'All Test Types' : testTypeFilter }]}
              onSelect={(_event, data) => {
                setTestTypeFilter(data.selection[0]?.id || 'all');
              }}
              variant="readonly"
            />
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div style={{ overflowX: 'auto' }}>
        <DataTable items={filteredTests} id="open-tests-table">
          <DataTableColumn label="Order Number" property="orderNumber" width="10rem">
            <OrderNumberCell />
          </DataTableColumn>
          <DataTableColumn label="Production Order" property="productionOrderNumber" width="12rem">
            <ProductionOrderCell />
          </DataTableColumn>
          <DataTableColumn label="Product" property="product" width="15rem">
            <ProductCell />
          </DataTableColumn>
          <DataTableColumn label="Test Name" property="testName" width="15rem">
            <TestNameCell />
          </DataTableColumn>
          <DataTableColumn label="Status" property="status" width="10rem">
            <StatusCell />
          </DataTableColumn>
          <DataTableColumn label="Result" property="result" width="10rem">
            <ResultCell />
          </DataTableColumn>
          <DataTableColumn label="Tested By" property="testedBy" width="10rem">
            <TestedByCell />
          </DataTableColumn>
          <DataTableColumn label="Warehouse" property="warehouse" width="10rem" />
          <DataTableColumn label="Actions" property="actions" width="14rem">
            <ActionsCell />
          </DataTableColumn>
        </DataTable>
      </div>

      {filteredTests.length === 0 && (
        <div className="slds-p-around_large slds-text-align_center">
          <Icon category="utility" name="search" size="large" colorVariant="light" />
          <p className="slds-text-heading_small slds-m-top_medium">No open tests found</p>
          <p className="slds-text-body_regular slds-text-color_weak slds-m-top_small">
            Try adjusting your search or filter criteria
          </p>
        </div>
      )}
    </div>
  );
};

export default OpenTestsComponent;
